import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query'
import {
  createDeliveryRouteContact,
  deleteDeliveryRouteContact,
  getDeliveryRouteContacts,
  updateDeliveryRouteContact,
} from './delivery-route-contacts.api'
import type { DeliveryRouteContactInput } from './delivery-route-contacts.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const deliveryRouteContactQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'deliveryRouteContact' }, { organizationId }),
  ],
  index: ({ organizationId }: OrganizationScope = {}) =>
    buildScopedAdminQueryKey(deliveryRouteContactQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
    }),
}

export const deliveryRouteContactQueries = {
  index: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: deliveryRouteContactQueryKeys.index({ organizationId }),
      queryFn: getDeliveryRouteContacts,
    }),
}

export function invalidateDeliveryRouteContactQueries(organizationId: OrganizationIdInput) {
  return queryClient.invalidateQueries({
    queryKey: deliveryRouteContactQueryKeys.all({ organizationId }),
  })
}

export function useDeliveryRouteContacts({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof deliveryRouteContactQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...deliveryRouteContactQueries.index({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useCreateDeliveryRouteContactMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: DeliveryRouteContactInput) =>
      createDeliveryRouteContact({ organizationId, ...values }),
    onSuccess: () => invalidateDeliveryRouteContactQueries(organizationId),
  })
}

export function useUpdateDeliveryRouteContactMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: DeliveryRouteContactInput & { deliveryRouteContactId: number }) =>
      updateDeliveryRouteContact({ organizationId, ...values }),
    onSuccess: () => invalidateDeliveryRouteContactQueries(organizationId),
  })
}

export function useDeleteDeliveryRouteContactMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (deliveryRouteContactId: number) =>
      deleteDeliveryRouteContact({ organizationId, deliveryRouteContactId }),
    onSuccess: () => invalidateDeliveryRouteContactQueries(organizationId),
  })
}
